import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useOrganizationsData } from "@/data/use-organizations-data";
import { getEnvironment } from "@/lib/environment";
import { useNavigate } from "@tanstack/react-router";
import { ChangeEvent, useState } from "react";
import { toast } from "sonner";

type CreateOrganizationModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const toSlug = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 23);

const SLUG_REGEX = /^[a-z0-9][a-z0-9-]{1,21}[a-z0-9]$/;

const validateSlug = (slug: string): string | null => {
  if (slug.length < 3) return "Slug must be at least 3 characters.";
  if (slug.length > 23) return "Slug must be at most 23 characters.";
  if (!SLUG_REGEX.test(slug))
    return "Slug must be kebab-case (lowercase letters, numbers, and hyphens only).";
  return null;
};

const organizationBucketPrefix =
  getEnvironment() === "staging" ? "nnr-org-staging" : "nnr-org";

export const CreateOrganizationModal: React.FC<
  CreateOrganizationModalProps
> = ({ open, onOpenChange }) => {
  const { createOrganization, isCreating } = useOrganizationsData();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);

  const bucketName = slug ? `${organizationBucketPrefix}-${slug}` : "";

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newName = event.target.value;
    setName(newName);
    if (!slugTouched) {
      const nextSlug = toSlug(newName);
      setSlug(nextSlug);
      setSlugError(validateSlug(nextSlug));
    }
  };

  const handleSlugChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newSlug = event.target.value;
    setSlug(newSlug);
    setSlugTouched(true);
    setSlugError(validateSlug(newSlug));
  };

  const handleClose = () => {
    setName("");
    setSlug("");
    setSlugTouched(false);
    setSlugError(null);
    onOpenChange(false);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateSlug(slug);
    if (error) {
      setSlugError(error);
      return;
    }

    try {
      const result = await createOrganization({ name, slug });
      toast.success(`Organization "${name}" created.`);
      handleClose();
      void navigate({
        to: "/admin/organizations/$organizationUuid",
        params: { organizationUuid: result.uuid },
      });
    } catch {
      toast.error("Failed to create organization. Please try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Organization</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="org-name">Organization Name</Label>
            <Input
              id="org-name"
              value={name}
              onChange={handleNameChange}
              placeholder="Acme Corp"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="org-slug">Slug</Label>
            <Input
              id="org-slug"
              value={slug}
              onChange={handleSlugChange}
              placeholder="acme-corp"
            />
            {slugError && (
              <p className="text-destructive text-sm">{slugError}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="org-bucket">
              Bucket Name (cannot be modified later)
            </Label>
            <Input
              id="org-bucket"
              value={bucketName}
              readOnly
              disabled
              placeholder={`${organizationBucketPrefix}-acme-corp`}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating || !name || !!slugError}>
              {isCreating ? "Creating…" : "Create Organization"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
